import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, useApp } from "../../context/AppContext";
import {
  getLiveTourPreviewDepartmentId,
  notifyLiveTourTargetsChanged,
  useLiveTourDepartmentPrep,
  useLiveTourOptional,
} from "../../context/LiveTourContext";
import { Overlay, AttachmentPreviewRow } from "../../components/common/UI";
import DepartmentDropdown from "../../components/common/DepartmentSelect";
import DepartmentSelectionRequired from "../../components/common/DepartmentSelectionRequired";
import GapQuestionComment, {
  SuggestedEvidenceTrigger,
} from "../../components/common/GapQuestionComment";
import PolicyGenerateModal from "../../components/common/PolicyGenerateModal";
import {
  GAP_POLICY_BLINK_CSS,
  GapPolicyFloatingAssistant,
  GapResultPolicyRecommendations,
} from "../../components/common/GapPolicyRecommendations";
import { collectGapPolicyRecommendations } from "../../data/gapPolicyMapping";
import { exportGapEvidenceExcel, exportGapEvidencePdf } from "../../utils/gapEvidenceExport";
import { showError } from "../../utils/sweetAlert";
import { useDepartments, useAutoSelectAssignedDepartment } from "../../hooks/useDepartments";
import { useAssessmentCycles } from "../../hooks/useAssessmentCycles";
import {
  afterValidationRender,
  isAttachmentBlocking,
  questionHintStyle,
  scrollToFirstInvalidQuestion,
} from "../../utils/formValidationFocus";
import {
  SECTIONS as DEFAULT_SECTIONS,
  R_CFG,
} from "../../components/common/gapAssessmentSections";
import {
  gapRecommendationLabel,
  isGapRecommendationObject,
  gapAttachmentPreviewPath,
} from "../../components/common/gapRecommendationUtils";
import { ArcGauge } from "../../components/common/ArcGauge";
import {
  ShieldCheck,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  TrendingUp,
  Users,
  Globe,
  Shield,
  Bell,
  BookOpen,
  Check,
  X,
  Minus,
  HelpCircle,
  Download,
  RefreshCw,
  Award,
  Zap,
  Menu,
  X as XIcon,
  ClipboardList,
  LayoutList,
  Building2,
  Lock,
  Eye,
  FileDown,
  FileSpreadsheet,
} from "lucide-react";

const RESPONSES = ["Yes", "Partially", "No", "N/A"];
const S_CFG = {
  High: { color: "#ef4444", bg: "#fff1f2" },
  Medium: { color: "#f59e0b", bg: "#fffbeb" },
  Low: { color: "#10b981", bg: "#f0fdf4" },
};

const SECTION_UI = Object.fromEntries(
  DEFAULT_SECTIONS.map((s) => [
    s.id,
    {
      icon: s.icon,
      accent: s.accent,
      accentLight: s.accentLight,
      description: s.description,
    },
  ]),
);

const FALLBACK_SECTION_UI = {
  icon: ShieldCheck,
  accent: "#6366f1",
  accentLight: "#eef2ff",
  description: "",
};

function detectLocked(draft) {
  if (!draft) return false;
  return Boolean(
    draft.is_locked ??
    draft.is_submitted ??
    draft.submitted ??
    (draft.status && String(draft.status).toLowerCase() === "submitted"),
  );
}

function normalizeGapQuestion(q) {
  return {
    ...q,
    required: q.required === false ? false : true,
    allow_attachment: Boolean(q.allow_attachment),
    attachment_required: Boolean(q.attachment_required),
  };
}

const GAP_SECTION_ORDER = DEFAULT_SECTIONS.map((s) => s.id);

function mergeApiSections(apiSections) {
  const mapped = (apiSections || []).map((sec) => {
    const ui = SECTION_UI[sec.id] || {};
    return {
      ...FALLBACK_SECTION_UI,
      ...ui,
      ...sec,
      icon: ui.icon ?? FALLBACK_SECTION_UI.icon,
      accent: ui.accent ?? sec.accent ?? FALLBACK_SECTION_UI.accent,
      accentLight:
        ui.accentLight ?? sec.accentLight ?? FALLBACK_SECTION_UI.accentLight,
      description: ui.description ?? sec.description ?? "",
      questions: (sec.questions || []).map(normalizeGapQuestion),
    };
  });
  const rank = Object.fromEntries(GAP_SECTION_ORDER.map((id, i) => [id, i]));
  mapped.sort((a, b) => {
    const av = rank[a.id];
    const bv = rank[b.id];
    const aKnown = av != null;
    const bKnown = bv != null;
    if (aKnown && bKnown) return av - bv;
    if (aKnown) return -1;
    if (bKnown) return 1;
    return String(a.id || "").localeCompare(String(b.id || ""));
  });
  return mapped;
}

function computeStats(answers, sections = DEFAULT_SECTIONS) {
  const all = sections.flatMap((s) => s.questions);
  const total = all.length;
  const yes = all.filter((q) => answers[q.id] === "Yes").length;
  const partial = all.filter((q) => answers[q.id] === "Partially").length;
  const no = all.filter((q) => answers[q.id] === "No").length;
  const na = all.filter((q) => answers[q.id] === "N/A").length;
  const applicable = total - na;
  const score =
    applicable > 0 ? Math.round(((yes + partial * 0.5) / applicable) * 100) : 0;
  const answered = all.filter((q) => answers[q.id]).length;
  return { total, yes, partial, no, na, score, answered };
}

function sectionPct(sec, answers) {
  const yes = sec.questions.filter((q) => answers[q.id] === "Yes").length;
  const partial = sec.questions.filter(
    (q) => answers[q.id] === "Partially",
  ).length;
  const na = sec.questions.filter((q) => answers[q.id] === "N/A").length;
  const applicable = sec.questions.length - na;
  return applicable > 0
    ? Math.round(((yes + partial * 0.5) / applicable) * 100)
    : 0;
}

function isGapQuestionRequired(q) {
  return q?.required === false ? false : true;
}

function isGapQuestionSatisfied(q, answers, attachments = {}) {
  if (!isGapQuestionRequired(q)) return true;
  if (!answers[q.id]) return false;
  return !isAttachmentBlocking(q, attachments, q.id);
}

function missingQuestionsInSection(
  sectionIdx,
  answers,
  sections = DEFAULT_SECTIONS,
  attachments = {},
) {
  return sections[sectionIdx].questions
    .filter((q) => !isGapQuestionSatisfied(q, answers, attachments))
    .map((q) => q.id);
}

function findFirstIncompleteSection(
  answers,
  sections = DEFAULT_SECTIONS,
  attachments = {},
) {
  for (let i = 0; i < sections.length; i++) {
    const questionIds = missingQuestionsInSection(
      i,
      answers,
      sections,
      attachments,
    );
    if (questionIds.length) return { stepIndex: i, questionIds };
  }
  return null;
}

function ScoreChip({ score }) {
  const color = score >= 75 ? "#10b981" : score >= 50 ? "#f59e0b" : "#ef4444";
  const label =
    score >= 75 ? "Compliant" : score >= 50 ? "Partial" : "Critical";
  return (
    <span
      style={{
        color,
        background: color + "15",
        border: `1px solid ${color}30`,
      }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold"
    >
      <span
        className="w-1.5 h-1.5 rounded-full inline-block"
        style={{ background: color }}
      />
      {label}
    </span>
  );
}

function QuestionInput({ q, ans, accent, onAnswer }) {
  const ft = q.field_type || "gap_choice";

  // gap_choice and boolean use the styled button grid
  if (ft === "gap_choice" || ft === "boolean") {
    const opts = ft === "boolean" ? ["Yes", "No"] : RESPONSES;
    return (
      <div className="grid grid-cols-2 gap-1.5">
        {opts.map((opt) => {
          const c = R_CFG[opt] || R_CFG["No"];
          const Ico = c.icon;
          const sel = ans === opt;
          return (
            <button
              key={opt}
              className={`r-btn opt-btn flex items-center gap-2 px-3 py-2.5 rounded-xl text-left transition-all${sel ? " sel" : ""}`}
              onClick={() => onAnswer(sel ? "" : opt)}
              style={{
                background: sel ? c.bg : "#ffffff",
                border: `1px solid ${sel ? c.border : "rgba(99,102,241,0.1)"}`,
                color: sel ? c.color : "#334155",
                boxShadow: sel ? `0 2px 12px ${c.color}30` : "none",
              }}
            >
              <div
                className="w-6 h-6 rounded-md flex items-center justify-center shrink-0"
                style={{
                  background: sel ? c.color : "#ffffff",
                  border: `1px solid ${sel ? c.color : "rgba(99,102,241,0.08)"}`,
                }}
              >
                <Ico size={12} className={sel ? "text-white" : "text-slate-600"} />
              </div>
              <div>
                <div className="text-[11px] font-semibold">{opt}</div>
                <div
                  className={`text-[9px] ${sel ? "font-semibold" : "font-normal"}`}
                  style={{ color: sel ? c.color : "#475569" }}
                >
                  {c.label}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  }

  // select / multiselect — render custom options from q.options
  if (ft === "select" || ft === "multiselect") {
    const options = (q.options || []).map((o) =>
      typeof o === "string" ? o : o.label,
    );
    const selected =
      ft === "multiselect"
        ? Array.isArray(ans)
          ? ans
          : ans
            ? [ans]
            : []
        : ans;
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {options.map((opt) => {
          const sel =
            ft === "multiselect" ? selected.includes(opt) : selected === opt;
          return (
            <button
              key={opt}
              onClick={() => {
                if (ft === "multiselect") {
                  const next = sel
                    ? selected.filter((v) => v !== opt)
                    : [...selected, opt];
                  onAnswer(next);
                } else {
                  onAnswer(sel ? "" : opt);
                }
              }}
              style={{
                padding: "7px 14px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 500,
                background: sel ? accent + "18" : "#f8fafc",
                border: `1px solid ${sel ? accent : "rgba(99,102,241,0.15)"}`,
                color: sel ? accent : "#334155",
                cursor: "pointer",
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  // text / textarea
  if (ft === "text" || ft === "textarea") {
    return (
      <textarea
        value={ans || ""}
        onChange={(e) => onAnswer(e.target.value)}
        rows={ft === "textarea" ? 3 : 1}
        placeholder="Enter your response…"
        style={{
          width: "100%",
          border: "1px solid rgba(99,102,241,0.2)",
          borderRadius: 10,
          padding: "9px 12px",
          fontSize: 12,
          resize: "vertical",
          outline: "none",
          fontFamily: "inherit",
        }}
      />
    );
  }

  // rating
  if (ft === "rating") {
    const options = (q.options || []).map((o) =>
      typeof o === "string" ? o : o.label,
    );
    const opts = options.length ? options : ["1", "2", "3", "4", "5"];
    return (
      <div style={{ display: "flex", gap: 6 }}>
        {opts.map((opt) => {
          const sel = ans === opt;
          return (
            <button
              key={opt}
              onClick={() => onAnswer(sel ? "" : opt)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 10,
                fontSize: 12,
                fontWeight: 700,
                background: sel ? accent : "#f8fafc",
                border: `1px solid ${sel ? accent : "rgba(99,102,241,0.15)"}`,
                color: sel ? "#fff" : "#334155",
                cursor: "pointer",
              }}
            >
              {opt}
            </button>
          );
        })}
      </div>
    );
  }

  // fallback — gap_choice
  return (
    <div
      style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 6 }}
    >
      {RESPONSES.map((opt) => {
        const c = R_CFG[opt];
        const Ico = c.icon;
        const sel = ans === opt;
        return (
          <button
            key={opt}
            className={`r-btn opt-btn${sel ? " sel" : ""}`}
            onClick={() => onAnswer(sel ? "" : opt)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: "10px 12px",
              borderRadius: 12,
              textAlign: "left",
              background: sel ? c.bg : "#ffffff",
              border: `1px solid ${sel ? c.border : "rgba(99,102,241,0.1)"}`,
              color: sel ? c.color : "#334155",
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: 7,
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: sel ? c.color : "rgba(255,255,255,1)",
                border: `1px solid ${sel ? c.color : "rgba(99,102,241,0.08)"}`,
              }}
            >
              <Ico size={12} style={{ color: sel ? "#ffffff" : "#475569" }} />
            </div>
            <div style={{ fontSize: 11, fontWeight: 600 }}>{opt}</div>
          </button>
        );
      })}
    </div>
  );
}

export default function DPDPGapAssessment() {
  const navigate = useNavigate();
  const { currentUser, showNotification } = useApp();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState({});
  const [owners, setOwners] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const orgName = currentUser?.organization_name || "";
  const [departmentId, setDepartmentId] = useState("");
  const { departments, loading: departmentsLoading } = useDepartments();
  useAutoSelectAssignedDepartment({
    departmentId,
    setDepartmentId,
    departments,
    loading: departmentsLoading,
  });
  const liveTour = useLiveTourOptional();
  const tourPreviewDepartmentId = getLiveTourPreviewDepartmentId(
    liveTour,
    departments,
    departmentsLoading,
  );
  const activeDepartmentId = departmentId || tourPreviewDepartmentId;
  useLiveTourDepartmentPrep(
    departmentId,
    setDepartmentId,
    departments,
    departmentsLoading,
  );
  const { currentCycleId, currentCycle } = useAssessmentCycles();
  // Tenant Admins can close a cycle from the Cycles tab. Once closed, the
  // form must stop accepting edits/submissions immediately — this mirrors
  // the per-submission `isLocked` state but is driven by cycle status
  // rather than an individual submission.
  const isCycleClosed =
    String(currentCycle?.status || "open").toLowerCase() === "closed";
  const [isLocked, setIsLocked] = useState(false);
  const [lockInfo, setLockInfo] = useState(null);
  const [attachments, setAttachments] = useState({});
  const [referenceEvidence, setReferenceEvidence] = useState({});
  const [uploadingFor, setUploadingFor] = useState({});
  const [usingReferenceFor, setUsingReferenceFor] = useState({});
  const [removingAttachmentId, setRemovingAttachmentId] = useState(null);
  const [loadingDept, setLoadingDept] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitResult, setSubmitResult] = useState(null);
  const [invalidQuestions, setInvalidQuestions] = useState([]);
  const [policyModalOpen, setPolicyModalOpen] = useState(false);
  const [generatePolicyIds, setGeneratePolicyIds] = useState([]);
  const [storeIndex, setStoreIndex] = useState(null);
  const policyStoreEnabled = (currentUser?.modules || []).includes("policy_store");
  const [sections, setSections] = useState(() => mergeApiSections(DEFAULT_SECTIONS));
  const saveTimer = useRef(null);
  const sectionNavRef = useRef(null);
  const sectionBtnRefs = useRef([]);
  const mainContentRef = useRef(null);

  const buildPayload = useCallback(
    () => ({
      org_name: orgName,
      department_id: departmentId || null,
      cycle_id: currentCycleId || null,
      step,
      answers,
      notes,
      owners,
    }),
    [orgName, departmentId, currentCycleId, step, answers, notes, owners],
  );

  useEffect(() => {
    const loadSections = () => {
      apiFetch("/gap-assessment/sections")
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data?.sections?.length) {
            setSections(mergeApiSections(data.sections));
          }
        })
        .catch(() => {});
    };
    loadSections();
    const onFocus = () => loadSections();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  useEffect(() => {
    if (!policyStoreEnabled) {
      setStoreIndex(null);
      return undefined;
    }
    let cancelled = false;
    const loadIndex = () => {
      apiFetch("/policy-store/recommendation-index")
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (!cancelled && data?.by_key) setStoreIndex(data);
        })
        .catch(() => {});
    };
    loadIndex();
    window.addEventListener("focus", loadIndex);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", loadIndex);
    };
  }, [policyStoreEnabled]);

  useEffect(() => {
    if (sections.length > 0 && step >= sections.length) {
      setStep(sections.length - 1);
    }
  }, [sections.length, step]);

  useEffect(() => {
    const questionIds = sections.flatMap((sec) =>
      (sec.questions || []).map((q) => q.id).filter(Boolean),
    );
    if (!questionIds.length) return;
    const params = new URLSearchParams({ question_ids: questionIds.join(",") });
    apiFetch(`/gap-assessment/reference-evidence?${params}`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (data?.items) setReferenceEvidence(data.items);
      })
      .catch(() => {});
  }, [sections]);

  const handleDepartmentChange = (e) => {
    clearTimeout(saveTimer.current);
    setDraftLoaded(false);
    setDepartmentId(e.target.value);
  };

  const loadDepartmentAssessment = useCallback(
    async (deptId) => {
      if (!deptId) {
        setDraftLoaded(true);
        setIsLocked(false);
        setLockInfo(null);
        return;
      }
      setLoadingDept(true);
      setDraftLoaded(false);
      try {
        const q = encodeURIComponent(deptId);
        const cycleQ = currentCycleId
          ? `&cycle_id=${encodeURIComponent(currentCycleId)}`
          : "";
        const [draftRes, attRes] = await Promise.all([
          apiFetch(`/gap-assessment/draft?department_id=${q}${cycleQ}`),
          apiFetch(`/gap-assessment/attachments?department_id=${q}${cycleQ}`),
        ]);
        let newAnswers = {};
        let newNotes = {};
        let newOwners = {};
        let newStep = 0;
        let locked = false;
        if (draftRes.ok) {
          const data = await draftRes.json();
          locked = detectLocked(data);
          newAnswers = data.answers || {};
          newNotes = data.notes || {};
          newOwners = data.owners || {};
          if (typeof data.step === "number") newStep = data.step;
          setLockInfo(
            locked
              ? {
                  submittedAt: data.submitted_at || data.updated_at || null,
                  submittedBy:
                    data.submitted_by_name || data.submitted_by || null,
                }
              : null,
          );
        } else {
          setLockInfo(null);
        }
        setIsLocked(locked);
        setAnswers(newAnswers);
        setNotes(newNotes);
        setOwners(newOwners);
        setStep(newStep);

        const grouped = {};
        if (attRes.ok) {
          const attData = await attRes.json();
          if (Array.isArray(attData)) {
            attData.forEach((row) => {
              if (!grouped[row.question_id]) grouped[row.question_id] = [];
              grouped[row.question_id].push(row);
            });
          }
        }
        setAttachments(grouped);
        setSubmitError("");
      } catch {
        setAnswers({});
        setNotes({});
        setOwners({});
        setAttachments({});
        setStep(0);
      } finally {
        setLoadingDept(false);
        setDraftLoaded(true);
      }
    },
    [currentCycleId],
  );

  useEffect(() => {
    loadDepartmentAssessment(activeDepartmentId);
  }, [activeDepartmentId, currentCycleId, loadDepartmentAssessment]);

  // Auto-save draft (debounced, per department)
  useEffect(() => {
    if (!draftLoaded) return;
    if (!departmentId) return;
    if (submitted) return;
    if (isLocked) return;
    if (isCycleClosed) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await apiFetch("/gap-assessment/draft", {
          method: "PUT",
          body: JSON.stringify(buildPayload()),
        });
      } catch {
        // silent — user can still submit manually
      } finally {
        setSaving(false);
      }
    }, 900);
    return () => clearTimeout(saveTimer.current);
  }, [
    draftLoaded,
    submitted,
    isLocked,
    isCycleClosed,
    departmentId,
    buildPayload,
  ]);

  const stats = useMemo(
    () => computeStats(answers, sections),
    [answers, sections],
  );
  const sec = sections[step];
  const SecIcon = sec?.icon || ShieldCheck;
  const secPct = useMemo(
    () => (sec ? sectionPct(sec, answers) : 0),
    [sec, answers],
  );
  const gapPolicyRecommendations = useMemo(
    () => collectGapPolicyRecommendations(answers, sections, orgName, storeIndex),
    [answers, sections, orgName, storeIndex],
  );
  const selectedDept = departments.find((d) => d.id === departmentId);

  const openPolicyStore = useCallback(
    (policyOrId) => {
      const policy =
        policyOrId && typeof policyOrId === "object" ? policyOrId : { id: policyOrId };
      const catalogueKey = policy.catalogueKey || policy.id || null;
      const documentId = policy.documentId || null;
      navigate("/user/policy-store", {
        state: {
          openPolicy: {
            id: documentId,
            catalogueKey: typeof catalogueKey === "string" ? catalogueKey : null,
            title: policy.title || "",
            act_ref: policy.actRef || policy.act_ref || "",
            description: policy.subtitle || policy.description || "",
            file_revision: policy.fileRevision || policy.file_revision || null,
            source: policy.source || null,
            is_default: Boolean(policy.isDefault),
          },
        },
      });
    },
    [navigate],
  );

  const openGeneratePolicies = useCallback((policyIds = []) => {
    setGeneratePolicyIds(policyIds.filter(Boolean));
    setPolicyModalOpen(true);
  }, []);

  const goTo = (i) => {
    if (i === step || transitioning) return;
    setTransitioning(true);
    setTimeout(() => {
      setStep(i);
      setTransitioning(false);
      mainContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    }, 220);
    setMobileNav(false);
  };

  useEffect(() => {
    const btn = sectionBtnRefs.current[step];
    if (!btn || !sectionNavRef.current) return;
    btn.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [step]);

  useEffect(() => {
    const onTourLastSection = () => {
      if (sections.length === 0) return;
      const last = sections.length - 1;
      setStep(last);
      setMobileNav(false);
      window.setTimeout(() => {
        mainContentRef.current?.scrollTo({ top: 0, behavior: "smooth" });
        notifyLiveTourTargetsChanged();
      }, 280);
    };
    window.addEventListener("live-tour:go-to-last-section", onTourLastSection);
    return () =>
      window.removeEventListener("live-tour:go-to-last-section", onTourLastSection);
  }, [sections.length]);

  const handleExport = async () => {
    try {
      const res = await apiFetch("/gap-assessment/export/csv", {
        method: "POST",
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `DPDP_Gap_Assessment_${orgName || "Results"}.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      // fallback: client-side CSV if API unavailable
      const rows = [
        [
          "Ref",
          "Section",
          "Question",
          "DPDP Ref",
          "Severity",
          "Response",
          "Notes",
          "Owner",
        ],
      ];
      sections.forEach((s) =>
        s.questions.forEach((q) => {
          rows.push([
            q.id,
            s.title,
            q.text,
            q.section,
            q.severity,
            answers[q.id] || "",
            notes[q.id] || "",
            owners[q.id] || "",
          ]);
        }),
      );
      const csv = rows
        .map((r) =>
          r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","),
        )
        .join("\n");
      const a = document.createElement("a");
      a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
      a.download = "DPDP_Gap_Assessment_Results.csv";
      a.click();
    }
  };

  const uploadAttachment = async (questionId, file) => {
    if (!departmentId) {
      throw new Error("Select a department before uploading attachments");
    }
    const fd = new FormData();
    fd.append("question_id", questionId);
    fd.append("department_id", departmentId);
    if (currentCycleId) fd.append("cycle_id", currentCycleId);
    fd.append("file", file);
    setUploadingFor((prev) => ({ ...prev, [questionId]: true }));
    try {
      const res = await apiFetch("/gap-assessment/attachment", {
        method: "POST",
        body: fd,
      });
      let data = {};
      try {
        data = await res.json();
      } catch {
        data = {};
      }
      if (!res.ok) {
        const detail = data.detail;
        const msg = Array.isArray(detail)
          ? detail[0]?.msg || detail[0]
          : detail || "Attachment upload failed";
        throw new Error(msg);
      }
      setAttachments((prev) => ({
        ...prev,
        [questionId]: [...(prev[questionId] || []), data],
      }));
    } catch (err) {
      setSubmitError(err.message || "Attachment upload failed");
      throw err;
    } finally {
      setUploadingFor((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const attachReferenceEvidence = async (questionId, evidence) => {
    if (!departmentId) {
      throw new Error("Select a department before attaching evidence");
    }
    setUsingReferenceFor((prev) => ({ ...prev, [questionId]: true }));
    try {
      const fd = new FormData();
      fd.append("reference_evidence_id", evidence.id);
      fd.append("question_id", questionId);
      fd.append("department_id", departmentId);
      if (currentCycleId) fd.append("cycle_id", currentCycleId);
      const res = await apiFetch("/gap-assessment/attachment/from-reference", {
        method: "POST",
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Could not attach suggested evidence");
      }
      setAttachments((prev) => ({
        ...prev,
        [questionId]: [...(prev[questionId] || []), data],
      }));
    } catch (err) {
      setSubmitError(err.message || "Could not attach suggested evidence");
      throw err;
    } finally {
      setUsingReferenceFor((prev) => ({ ...prev, [questionId]: false }));
    }
  };

  const removeAttachment = async (questionId, file) => {
    if (!file?.id) return;
    if (isLocked || isCycleClosed) {
      setSubmitError("This assessment is locked and attachments cannot be removed.");
      return;
    }
    setRemovingAttachmentId(file.id);
    try {
      const res = await apiFetch(`/gap-assessment/attachment/${file.id}`, {
        method: "DELETE",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data.detail || "Could not remove attachment");
      }
      setAttachments((prev) => ({
        ...prev,
        [questionId]: (prev[questionId] || []).filter((item) => item.id !== file.id),
      }));
    } catch (err) {
      setSubmitError(err.message || "Could not remove attachment");
      throw err;
    } finally {
      setRemovingAttachmentId(null);
    }
  };

  const handleSubmit = async () => {
    if (isLocked) {
      setSubmitError(
        "This assessment has already been submitted and is locked for editing. Contact your tenant admin to reopen it.",
      );
      return;
    }
    if (isCycleClosed) {
      setSubmitError(
        "This assessment cycle is closed. Contact your tenant admin to reopen it before submitting.",
      );
      return;
    }
    if (!departmentId) {
      setSubmitError("Please select your department before submitting.");
      return;
    }
    const incomplete = findFirstIncompleteSection(answers, sections, attachments);
    if (incomplete) {
      const jump = incomplete.stepIndex !== step;
      if (jump) goTo(incomplete.stepIndex);
      setInvalidQuestions(incomplete.questionIds);
      setSubmitError(
        `Please complete ${incomplete.questionIds.length} required question(s) before submitting.`,
      );
      afterValidationRender(() => {
        setTimeout(
          () =>
            scrollToFirstInvalidQuestion(
              incomplete.questionIds,
              mainContentRef,
            ),
          jump ? 280 : 0,
        );
      });
      return;
    }
    setInvalidQuestions([]);
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await apiFetch("/gap-assessment/submit", {
        method: "POST",
        body: JSON.stringify(buildPayload()),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Submission failed");
      }
      const data = await res.json();
      setSubmitResult(data);
      setSubmitted(true);
    } catch (e) {
      setSubmitError(e.message || "Could not submit assessment");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    if (isLocked || isCycleClosed) {
      goTo(Math.min(sections.length - 1, step + 1));
      return;
    }
    const missing = missingQuestionsInSection(
      step,
      answers,
      sections,
      attachments,
    );
    if (missing.length) {
      setInvalidQuestions(missing);
      setSubmitError(
        `Please complete ${missing.length} required question(s) before continuing.`,
      );
      afterValidationRender(() => {
        scrollToFirstInvalidQuestion(missing, mainContentRef);
      });
      return;
    }
    setInvalidQuestions([]);
    setSubmitError("");
    goTo(step + 1);
  };

  if (submitted) {
    const resultStats = submitResult
      ? {
          score: submitResult.score,
          total: submitResult.total_questions,
          answered: submitResult.answered,
          yes: submitResult.yes,
          partial: submitResult.partial,
          no: submitResult.no,
          na: submitResult.na,
        }
      : stats;
    return (
      <div
        style={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: "#ffffff",
          fontFamily: "'DM Sans', system-ui, sans-serif",
        }}
      >
        <style>{`
          .gap-result-scroll {
            scroll-behavior: smooth;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior: contain;
            touch-action: pan-y;
            overflow-y: auto;
            overflow-x: hidden;
          }
          .gap-result-scroll::-webkit-scrollbar { width: 6px; }
          .gap-result-scroll::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 9px; }
          .gap-result-scroll::-webkit-scrollbar-thumb {
            background: linear-gradient(180deg, rgba(99,102,241,0.55), rgba(139,92,246,0.45));
            border-radius: 9px;
          }
          ${GAP_POLICY_BLINK_CSS}
        `}</style>
        <ResultScreen
          stats={resultStats}
          answers={answers}
          notes={notes}
          owners={owners}
          sections={sections}
          submitResult={submitResult}
          orgName={orgName}
          departmentId={departmentId}
          cycleId={currentCycleId}
          departmentLabel={
            submitResult?.department_name ||
            departments.find((d) => d.id === departmentId)?.name ||
            ""
          }
          onViewPolicy={openPolicyStore}
          onGenerate={openGeneratePolicies}
          storeIndex={storeIndex}
          onReset={() => {
            setSubmitted(false);
            setSubmitResult(null);
            setAnswers({});
            setNotes({});
            setOwners({});
            setAttachments({});
            setStep(0);
            setDraftLoaded(true);
          }}
        />
      </div>
    );
  }

  const SidebarInner = () => (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Fixed top: compliance score only */}
      <div style={{ padding: "12px 12px 0", flexShrink: 0 }}>
        <div
          data-tour-id="tour-gap-progress"
          style={{
            background: "#ffffff",
            border: "1px solid #e2e8f0",
            borderRadius: 16,
            padding: 16,
            marginBottom: 10,
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#1e293b",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
              }}
            >
              Compliance Score
            </span>
            <ScoreChip score={stats.score} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ position: "relative", flexShrink: 0 }}>
              <ArcGauge
                value={stats.score}
                size={68}
                stroke={7}
                color={
                  stats.score >= 75
                    ? "#10b981"
                    : stats.score >= 50
                      ? "#f59e0b"
                      : "#ef4444"
                }
              />
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                }}
              >
                <span
                  style={{
                    fontSize: 15,
                    fontWeight: 800,
                    color: "#0f172a",
                    lineHeight: 1,
                  }}
                >
                  {stats.score}%
                </span>
              </div>
            </div>
            <div style={{ flex: 1 }}>
              {[
                { l: "Yes", v: stats.yes, c: "#047857" },
                { l: "Partial", v: stats.partial, c: "#b45309" },
                { l: "No", v: stats.no, c: "#b91c1c" },
                { l: "N/A", v: stats.na, c: "#334155" },
              ].map((x) => (
                <div
                  key={x.l}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    marginBottom: 4,
                  }}
                >
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: x.c,
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: 10, color: "#1e293b", flex: 1 }}>
                    {x.l}
                  </span>
                  <span
                    style={{ fontSize: 11, fontWeight: 700, color: "#0f172a" }}
                  >
                    {x.v}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div
            style={{
              marginTop: 10,
              height: 4,
              borderRadius: 9,
              background: "#ffffff",
              overflow: "hidden",
              display: "flex",
            }}
          >
            {stats.yes > 0 && (
              <div
                style={{
                  width: `${(stats.yes / stats.total) * 100}%`,
                  background: "#10b981",
                  height: "100%",
                  transition: "width 0.7s ease",
                }}
              />
            )}
            {stats.partial > 0 && (
              <div
                style={{
                  width: `${(stats.partial / stats.total) * 100}%`,
                  background: "#f59e0b",
                  height: "100%",
                  transition: "width 0.7s ease",
                }}
              />
            )}
            {stats.no > 0 && (
              <div
                style={{
                  width: `${(stats.no / stats.total) * 100}%`,
                  background: "#ef4444",
                  height: "100%",
                  transition: "width 0.7s ease",
                }}
              />
            )}
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 5,
              fontSize: 10,
              color: "#1e293b",
            }}
          >
            <span>{stats.answered} answered</span>
            <span>{stats.total} total</span>
          </div>
        </div>
      </div>

      {/* Scrollable: section navigation */}
      <div
        ref={sectionNavRef}
        data-tour-id="tour-gap-sections"
        className="gap-scroll gap-sidebar-scroll"
        style={{
          flex: 1,
          minHeight: 0,
          padding: "0 12px 16px",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(99,102,241,0.6) #e2e8f0",
        }}
      >
        <div
          style={{
            fontSize: 10,
            fontWeight: 700,
            color: "#0f172a",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            padding: "2px 4px 8px",
          }}
        >
          Sections
        </div>

        {sections.map((s, i) => {
          const requiredQs = s.questions.filter(isGapQuestionRequired);
          const done = requiredQs.filter((q) => answers[q.id]).length;
          const complete =
            requiredQs.length === 0 || done === requiredQs.length;
          const active = i === step;
          return (
            <button
              key={s.id}
              ref={(el) => {
                sectionBtnRefs.current[i] = el;
              }}
              onClick={() => goTo(i)}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "10px 10px",
                borderRadius: 12,
                border: `1px solid ${active ? s.accent + "35" : "transparent"}`,
                background: active
                  ? `linear-gradient(135deg, ${s.accent}18 0%, ${s.accent}0a 100%)`
                  : "transparent",
                cursor: "pointer",
                transition: "all 0.15s ease",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 9,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: active
                    ? `linear-gradient(135deg, ${s.accent}cc, ${s.accent}66)`
                    : "linear-gradient(135deg, #f1f5f9 0%, #e8eaf6 100%)",
                  border: `1px solid ${active ? s.accent + "50" : "rgba(99,102,241,0.1)"}`,
                  boxShadow: active ? `0 2px 8px ${s.accent}35` : "none",
                }}
              >
                {s.icon && (
                  <s.icon
                    size={13}
                    style={{ color: active ? "#fff" : "#0f172a" }}
                  />
                )}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#0f172a",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.title}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: active ? s.accent : "#1e293b",
                    marginTop: 1,
                    fontWeight: 500,
                  }}
                >
                  {done}/{s.questions.length} answered
                </div>
              </div>
              {complete && (
                <CheckCircle2
                  size={12}
                  style={{ color: "#10b981", flexShrink: 0 }}
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      className="gap-assessment-root"
      style={{
        flex: 1,
        minHeight: 0,
        height: "100%",
        overflow: "hidden",
        background: "#ffffff",
        fontFamily: "'DM Sans', system-ui, -apple-system, sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300..700;1,9..40,300..700&display=swap');
        .gap-assessment-root, .gap-assessment-root * { box-sizing: border-box; }
        .gap-scroll {
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          touch-action: pan-y;
          overflow-y: auto !important;
          overflow-x: hidden;
        }
        .gap-sidebar-scroll {
          flex: 1 1 0;
          min-height: 0;
        }
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #e2e8f0; border-radius: 9px; }
        ::-webkit-scrollbar-thumb { background: linear-gradient(180deg, rgba(99,102,241,0.55), rgba(139,92,246,0.45)); border-radius: 9px; }
        ::-webkit-scrollbar-thumb:hover { background: linear-gradient(180deg, rgba(99,102,241,0.75), rgba(139,92,246,0.65)); }
        .q-fade-in { animation: qFadeIn 0.28s cubic-bezier(0.4,0,0.2,1); }
        .q-fade-out { animation: qFadeOut 0.18s cubic-bezier(0.4,0,0.2,1); }
        @keyframes qFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes qFadeOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(-8px); } }
        .r-btn { transition: all 0.16s cubic-bezier(0.4,0,0.2,1); border: none; cursor: pointer; }
        .r-btn:hover { transform: translateY(-1px); filter: brightness(1.05); }
        .r-btn:active { transform: translateY(0); }
        .opt-btn { background: #ffffff !important; }
        .opt-btn:hover { background: #f1f5f9 !important; }
        .opt-btn.sel { background: inherit !important; }
        input, textarea { font-family: inherit; }
        textarea { resize: vertical; }
        button { font-family: inherit; cursor: pointer; }
        .nav-dot { transition: all 0.25s ease; }
        ${GAP_POLICY_BLINK_CSS}
      `}</style>

      {/* Header */}
      <header
        data-tour-id="tour-gap-header"
        style={{
          background: "#ffffff",
          boxShadow:
            "0 1px 0 0 rgba(99,102,241,0.18), 0 2px 8px rgba(0,0,0,0.04)",
          borderBottom: "1px solid rgba(99,102,241,0.10)",
          padding: "10px 16px",
          display: "flex",
          alignItems: "center",
          gap: 12,
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => setMobileNav(true)}
          style={{
            display: "none",
            padding: 6,
            borderRadius: 8,
            background: "#ffffff",
            border: "1px solid rgba(99,102,241,0.15)",
            color: "#1e293b",
            flexShrink: 0,
          }}
          className="mob-menu-btn"
        >
          <Menu size={18} />
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background:
                "linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow:
                "0 4px 18px rgba(99,102,241,0.55), 0 2px 8px rgba(139,92,246,0.35)",
            }}
          >
            <ShieldCheck size={16} color="#fff" />
          </div>
          <div>
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: "#0f172a",
                lineHeight: 1.2,
              }}
            >
              DPDP Act 2023
            </div>
            <div style={{ fontSize: 10, color: "#1e293b", lineHeight: 1.2 }}>
              Gap Assessment
              {currentCycle?.name ? ` · ${currentCycle.name}` : ""}
            </div>
          </div>
        </div>

        <div
          data-tour-id="tour-gap-department"
          style={{
            flex: 1,
            maxWidth: 280,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <DepartmentDropdown
            value={departmentId}
            onChange={handleDepartmentChange}
            departments={departments}
            loading={departmentsLoading}
            lockToAssigned
            style={{ width: "100%" }}
          />
          {(isLocked || isCycleClosed) && departmentId && (
            <span
              title={
                isLocked
                  ? "This department's assessment has already been submitted and is read-only"
                  : "This assessment cycle is closed — the form is read-only until an admin reopens it"
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 10px",
                borderRadius: 999,
                background: "linear-gradient(135deg,#f8fafc,#eef2f7)",
                border: "1px solid #cbd5e1",
                color: "#475569",
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                flexShrink: 0,
              }}
            >
              <Lock size={11} /> {isLocked ? "Locked" : "Cycle Closed"}
            </span>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "6px 14px",
              borderRadius: 10,
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.15)",
            }}
          >
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {stats.score}%
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>
                Score
              </div>
            </div>
            <div
              style={{
                width: 1,
                height: 22,
                background: "rgba(99,102,241,0.2)",
              }}
            />
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>
                {stats.answered}
                <span style={{ color: "#1e293b", fontWeight: 400 }}>
                  /{stats.total}
                </span>
              </div>
              <div style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>
                Done
              </div>
            </div>
          </div>
          {loadingDept && (
            <span style={{ fontSize: 10, color: "#475569", fontWeight: 500 }}>
              Loading…
            </span>
          )}
          {saving && (
            <span style={{ fontSize: 10, color: "#475569", fontWeight: 500 }}>
              Saving...
            </span>
          )}
          <button
            data-tour-id="tour-gap-view-table"
            onClick={() => {
              navigate(
                departmentId
                  ? `/user/gap-assessment/submissions?department_id=${encodeURIComponent(departmentId)}`
                  : "/user/gap-assessment/submissions",
              );
            }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 10,
              background: "#ffffff",
              color: "#6366f1",
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid rgba(99,102,241,0.25)",
              boxShadow: "0 2px 8px rgba(99,102,241,0.1)",
            }}
          >
            <LayoutList size={13} />
            View Table
          </button>
          <button
            onClick={handleExport}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "7px 14px",
              borderRadius: 10,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              color: "#fff",
              fontSize: 11,
              fontWeight: 600,
              boxShadow: "0 4px 14px rgba(99,102,241,0.35)",
              border: "none",
            }}
          >
            <Download size={13} />
            Export
          </button>
        </div>
      </header>

      {!departmentId && (
        <div
          style={{
            flexShrink: 0,
            padding: "8px 16px",
            background: "#fffbeb",
            borderBottom: "1px solid rgba(245,158,11,0.2)",
            fontSize: 11,
            color: "#92400e",
            textAlign: "center",
          }}
        >
          Select a department above — each department has its own responses and
          recommendations.
        </div>
      )}

      {isLocked && departmentId && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #cbd5e1",
            fontSize: 11.5,
            color: "#475569",
            textAlign: "center",
          }}
        >
          <Lock size={12} />
          This department&rsquo;s assessment has already been submitted
          {currentCycle?.name ? ` for ${currentCycle.name}` : ""}. You&rsquo;re
          viewing the submitted responses in read-only mode.
          {lockInfo?.submittedAt && (
            <span style={{ color: "#94a3b8" }}>
              &nbsp;· Submitted{" "}
              {new Date(lockInfo.submittedAt).toLocaleDateString()}
            </span>
          )}
        </div>
      )}

      {!isLocked && isCycleClosed && departmentId && (
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            padding: "8px 16px",
            background: "#f8fafc",
            borderBottom: "1px solid #cbd5e1",
            fontSize: 11.5,
            color: "#475569",
            textAlign: "center",
          }}
        >
          <Lock size={12} />
          {currentCycle?.name
            ? `${currentCycle.name} is`
            : "This assessment cycle is"}{" "}
          closed. The form is read-only until your tenant admin reopens the
          cycle.
        </div>
      )}

      <style>{`@media (max-width: 900px) { .mob-menu-btn { display: flex !important; } }`}</style>

      <div
        style={{
          display: "flex",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        {!activeDepartmentId ? (
          <DepartmentSelectionRequired message="Please select a department from the dropdown in the header to load, view, or fill the assessment." />
        ) : (
          <>
        {/* Mobile overlay */}
        {mobileNav && (
          <Overlay
            open={mobileNav}
            onClose={() => setMobileNav(false)}
            className="bg-slate-900/25 backdrop-blur-sm"
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 280,
                background: "#ffffff",
                borderRight: "1px solid rgba(99,102,241,0.15)",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "12px 16px",
                  borderBottom: "1px solid rgba(99,102,241,0.12)",
                }}
              >
                <span
                  style={{ fontSize: 12, fontWeight: 600, color: "#0f172a" }}
                >
                  Navigation
                </span>
                <button
                  onClick={() => setMobileNav(false)}
                  style={{
                    color: "#1e293b",
                    background: "none",
                    border: "none",
                  }}
                >
                  <XIcon size={16} />
                </button>
              </div>
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <SidebarInner />
              </div>
            </div>
          </Overlay>
        )}

        {/* Desktop sidebar */}
        <aside
          data-tour-id="tour-gap-sidebar"
          className="desktop-sidebar"
          style={{
            width: 260,
            flexShrink: 0,
            alignSelf: "stretch",
            background: "#ffffff",
            borderRight: "1px solid rgba(99,102,241,0.14)",
            boxShadow: "2px 0 12px rgba(99,102,241,0.06)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <style>{`@media (max-width: 900px) { .desktop-sidebar { display: none !important; } }`}</style>
          <SidebarInner />
        </aside>

        {/* Main */}
        <main
          ref={mainContentRef}
          data-tour-id="tour-gap-questions"
          className="gap-scroll"
          style={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            background: "#ffffff",
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(99,102,241,0.3) #e2e8f0",
            position: "relative",
          }}
        >
            <div
              style={{
                width: "100%",
                padding: "20px 24px 40px",
              }}
            >
              {/* Section banner */}
              <div
                data-tour-id="tour-gap-section-banner"
                style={{
                  borderRadius: 20,
                  padding: "18px 20px",
                  marginBottom: 16,
                  background: "#ffffff",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                  opacity: loadingDept ? 0.6 : 1,
                  transition: "opacity 0.2s",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "flex-start", gap: 14 }}
                >
                  <div
                    style={{
                      width: 46,
                      height: 46,
                      borderRadius: 14,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                      background: `linear-gradient(135deg, ${sec.accent}ee 0%, ${sec.accent}88 100%)`,
                      border: `1px solid ${sec.accent}40`,
                      boxShadow: `0 4px 16px ${sec.accent}40`,
                    }}
                  >
                    <SecIcon size={20} style={{ color: "#fff" }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        flexWrap: "wrap",
                      }}
                    >
                      <h2
                        style={{
                          fontSize: 16,
                          fontWeight: 700,
                          color: "#0f172a",
                        }}
                      >
                        {sec.title}
                      </h2>
                      <span
                        style={{
                          fontSize: 10,
                          fontFamily: "monospace",
                          fontWeight: 600,
                          padding: "2px 8px",
                          borderRadius: 6,
                          background: sec.accent + "1a",
                          color: sec.accent,
                          border: `1px solid ${sec.accent}28`,
                        }}
                      >
                        {sec.code}
                      </span>
                      {selectedDept && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 600,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#eef2ff",
                            color: "#4338ca",
                            border: "1px solid rgba(99,102,241,0.2)",
                          }}
                        >
                          <Building2 size={10} />
                          {selectedDept.name}
                        </span>
                      )}
                      {(isLocked || isCycleClosed) && (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            fontSize: 10,
                            fontWeight: 700,
                            padding: "2px 8px",
                            borderRadius: 6,
                            background: "#f1f5f9",
                            color: "#64748b",
                            border: "1px solid #cbd5e1",
                          }}
                        >
                          <Lock size={9} /> View Only
                        </span>
                      )}
                    </div>
                    <p style={{ fontSize: 12, color: "#1e293b", marginTop: 3 }}>
                      {sec.description}
                    </p>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        marginTop: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      <span
                        style={{
                          fontSize: 11,
                          color: "#1e293b",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <ClipboardList size={11} /> {sec.questions.length}{" "}
                        questions
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: "#1e293b",
                          display: "flex",
                          alignItems: "center",
                          gap: 5,
                        }}
                      >
                        <TrendingUp size={11} /> Section {step + 1} of{" "}
                        {sections.length}
                      </span>
                      <div
                        style={{
                          marginLeft: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: sec.accent,
                          }}
                        >
                          {secPct}%
                        </span>
                        <div
                          style={{
                            width: 80,
                            height: 5,
                            borderRadius: 9,
                            background: "#f1f5f9",
                            overflow: "hidden",
                          }}
                        >
                          <div
                            style={{
                              width: `${secPct}%`,
                              height: "100%",
                              background: sec.accent,
                              borderRadius: 9,
                              transition: "width 0.7s ease",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Questions */}
              <div style={{ position: "relative" }}>
                {(isLocked || isCycleClosed) && (
                  <div
                    aria-hidden="true"
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: 5,
                      cursor: "not-allowed",
                      background:
                        "repeating-linear-gradient(135deg, rgba(148,163,184,0.05) 0px, rgba(148,163,184,0.05) 14px, rgba(148,163,184,0.09) 14px, rgba(148,163,184,0.09) 28px)",
                      borderRadius: 16,
                    }}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 12,
                    pointerEvents: isLocked || isCycleClosed ? "none" : "auto",
                    opacity: isLocked || isCycleClosed ? 0.72 : 1,
                    filter:
                      isLocked || isCycleClosed
                        ? "grayscale(0.25) saturate(0.85)"
                        : "none",
                    userSelect: isLocked || isCycleClosed ? "text" : "auto",
                    transition: "opacity 0.2s ease, filter 0.2s ease",
                  }}
                >
                  {sec.questions.map((q, qi) => {
                    const ans = answers[q.id];
                    const cfg = ans ? R_CFG[ans] : null;
                    const sev = S_CFG[q.severity];

                    return (
                      <div
                        key={q.id}
                        id={`question-${q.id}`}
                        data-question-id={q.id}
                        data-field-wrapper
                        data-tour-id={qi === 0 ? "tour-gap-answer-area" : undefined}
                        style={{
                          borderRadius: 18,
                          overflow: "hidden",
                          background: "rgba(255,255,255,1)",
                          border: `1px solid ${ans && cfg ? cfg.border : "rgba(99,102,241,0.12)"}`,
                          transition: "all 0.25s ease",
                          boxShadow:
                            ans && cfg ? `0 2px 12px ${cfg.color}22` : "none",
                        }}
                      >
                        <div style={{ padding: "16px 18px" }}>
                          {invalidQuestions.includes(q.id) &&
                            isGapQuestionRequired(q) && (
                            <div style={questionHintStyle}>
                              Please select a response to continue.
                            </div>
                          )}
                          {/* Q header */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 12,
                              marginBottom: 12,
                            }}
                          >
                            <div
                              style={{
                                width: 26,
                                height: 26,
                                borderRadius: 8,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                                background: `linear-gradient(135deg, ${sec.accent}28 0%, ${sec.accent}14 100%)`,
                                border: `1px solid ${sec.accent}35`,
                                fontSize: 11,
                                fontWeight: 800,
                                color: sec.accent,
                                fontFamily: "monospace",
                              }}
                            >
                              {qi + 1}
                            </div>
                            <div style={{ flex: 1 }}>
                              <p
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: "#0f172a",
                                  lineHeight: 1.55,
                                }}
                              >
                                {q.text}
                                {isGapQuestionRequired(q) ? (
                                  <span style={{ color: "#ef4444" }}> *</span>
                                ) : (
                                  <span
                                    style={{
                                      color: "#64748b",
                                      fontSize: 11,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {" "}
                                    (Optional)
                                  </span>
                                )}
                              </p>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 8,
                                  marginTop: 7,
                                  flexWrap: "wrap",
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontFamily: "monospace",
                                    fontWeight: 600,
                                    padding: "2px 6px",
                                    borderRadius: 5,
                                    background: "#f1f5f9",
                                    color: "#1e293b",
                                    border: "1px solid rgba(99,102,241,0.1)",
                                  }}
                                >
                                  {q.id}
                                </span>
                                {referenceEvidence[q.id] && (
                                  <SuggestedEvidenceTrigger
                                    evidence={referenceEvidence[q.id]}
                                    usingReference={!!usingReferenceFor[q.id]}
                                    onUseReference={(evidence) =>
                                      attachReferenceEvidence(q.id, evidence)
                                    }
                                    attachDisabledReason={
                                      !departmentId ? "Select a department before attaching evidence." : ""
                                    }
                                    disabled={isLocked || isCycleClosed || !!uploadingFor[q.id]}
                                  />
                                )}
                                <span
                                  style={{ fontSize: 10, color: "#1e293b" }}
                                >
                                  {q.section}
                                </span>
                                <span
                                  style={{
                                    fontSize: 9,
                                    fontWeight: 600,
                                    padding: "2px 8px",
                                    borderRadius: 20,
                                    background: sev.bg + "1a",
                                    color: sev.color,
                                    border: `1px solid ${sev.color}28`,
                                  }}
                                >
                                  {q.severity} Risk
                                </span>
                                {ans && cfg && (
                                  <span
                                    style={{
                                      fontSize: 9,
                                      fontWeight: 600,
                                      padding: "2px 8px",
                                      borderRadius: 20,
                                      background: cfg.color + "15",
                                      color: cfg.color,
                                      border: `1px solid ${cfg.color}28`,
                                      marginLeft: "auto",
                                    }}
                                  >
                                    {cfg.label}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Response options */}
                          <QuestionInput
                            q={q}
                            ans={ans}
                            accent={sec.accent}
                            onAnswer={(val) =>
                              setAnswers((p) => ({ ...p, [q.id]: val }))
                            }
                          />

                          <GapQuestionComment
                            key={q.id}
                            questionId={q.id}
                            note={notes[q.id] || ""}
                            owner={owners[q.id] || ""}
                            onNoteChange={(value) =>
                              setNotes((p) => ({ ...p, [q.id]: value }))
                            }
                            onOwnerChange={(value) =>
                              setOwners((p) => ({ ...p, [q.id]: value }))
                            }
                            attachments={attachments[q.id] || []}
                            uploading={!!uploadingFor[q.id]}
                            onUpload={uploadAttachment}
                            onRemove={removeAttachment}
                            removingAttachmentId={removingAttachmentId}
                            allowAttachment={q.allow_attachment}
                            attachmentRequired={q.attachment_required}
                            disabled={isLocked || isCycleClosed}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {(isLocked || isCycleClosed) && (
                  <div
                    style={{
                      position: "sticky",
                      bottom: 12,
                      display: "flex",
                      justifyContent: "center",
                      marginTop: 14,
                      pointerEvents: "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "7px 16px",
                        borderRadius: 999,
                        background: "rgba(15,23,42,0.85)",
                        color: "#f8fafc",
                        fontSize: 11,
                        fontWeight: 600,
                        boxShadow: "0 6px 20px rgba(15,23,42,0.25)",
                        backdropFilter: "blur(4px)",
                      }}
                    >
                      <Lock size={12} />{" "}
                      {isLocked
                        ? "Submitted — read only"
                        : "Cycle closed — read only"}
                    </div>
                  </div>
                )}
              </div>

              {submitError && (
                <p
                  style={{
                    fontSize: 11,
                    color: "#b91c1c",
                    marginTop: 12,
                    fontWeight: 500,
                  }}
                >
                  {submitError}
                </p>
              )}

              {/* Nav footer */}
              <div
                data-tour-id="tour-gap-nav-footer"
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginTop: 20,
                  gap: 12,
                }}
              >
                <button
                  data-tour-id="tour-gap-prev"
                  onClick={() => goTo(Math.max(0, step - 1))}
                  disabled={step === 0}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 7,
                    padding: "9px 16px",
                    borderRadius: 12,
                    background: "#ffffff",
                    border: "1px solid rgba(99,102,241,0.18)",
                    color: step === 0 ? "#475569" : "#334155",
                    fontSize: 12,
                    fontWeight: 500,
                    cursor: step === 0 ? "not-allowed" : "pointer",
                    opacity: step === 0 ? 0.4 : 1,
                    transition: "all 0.15s",
                  }}
                >
                  <ChevronLeft size={15} /> Previous
                </button>

                <div
                  className="gap-scroll"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    maxWidth: "100%",
                    overflowX: "auto",
                    paddingBottom: 2,
                    scrollbarWidth: "none",
                  }}
                >
                  {sections.map((s, i) => (
                    <button
                      key={i}
                      onClick={() => goTo(i)}
                      className="nav-dot"
                      style={{
                        height: 6,
                        borderRadius: 9,
                        border: "none",
                        cursor: "pointer",
                        width: i === step ? 22 : 6,
                        background:
                          i === step
                            ? sec.accent
                            : i < step
                              ? sec.accent + "55"
                              : "rgba(99,102,241,0.2)",
                      }}
                    />
                  ))}
                </div>

                {isLocked || isCycleClosed ? (
                  step < sections.length - 1 ? (
                    <button
                      onClick={handleNext}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "9px 18px",
                        borderRadius: 12,
                        background: "#ffffff",
                        color: "#334155",
                        fontSize: 12,
                        fontWeight: 600,
                        border: "1px solid rgba(99,102,241,0.18)",
                        cursor: "pointer",
                        transition: "all 0.15s",
                      }}
                    >
                      Next <ChevronRight size={15} />
                    </button>
                  ) : (
                    <span
                      data-tour-id="tour-gap-submit-btn"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "9px 20px",
                        borderRadius: 12,
                        background: "#f1f5f9",
                        color: "#64748b",
                        fontSize: 12,
                        fontWeight: 700,
                        border: "1px solid #cbd5e1",
                      }}
                    >
                      <Lock size={14} />{" "}
                      {isLocked
                        ? "Locked — Already Submitted"
                        : "Locked — Cycle Closed"}
                    </span>
                  )
                ) : step < sections.length - 1 ? (
                  <button
                    data-tour-id="tour-gap-next"
                    onClick={handleNext}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 18px",
                      borderRadius: 12,
                      background: `linear-gradient(135deg, ${sec.accent}, ${sec.accent}bb)`,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 600,
                      border: "none",
                      boxShadow: `0 4px 16px ${sec.accent}40`,
                      transition: "all 0.15s",
                    }}
                  >
                    Next <ChevronRight size={15} />
                  </button>
                ) : (
                  <button
                    data-tour-id="tour-gap-submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 7,
                      padding: "9px 20px",
                      borderRadius: 12,
                      background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      border: "none",
                      boxShadow: "0 4px 20px rgba(99,102,241,0.45)",
                      transition: "all 0.15s",
                      opacity: submitting ? 0.7 : 1,
                      cursor: submitting ? "not-allowed" : "pointer",
                    }}
                  >
                    <Zap size={14} />{" "}
                    {submitting ? "Submitting..." : "Submit Assessment"}
                  </button>
                )}
              </div>
            </div>
        </main>
          </>
        )}
      </div>

      <PolicyGenerateModal
        open={policyModalOpen}
        onClose={() => setPolicyModalOpen(false)}
        organizationName={orgName}
        userKey={currentUser?.id ?? currentUser?.email ?? orgName ?? "default"}
        initialSelectedIds={generatePolicyIds}
        onSuccess={(data) => {
          showNotification(
            data?.message || "Policies saved to Policy Store",
            "success",
          );
          setPolicyModalOpen(false);
          navigate("/user/policy-store");
        }}
      />

      <GapPolicyFloatingAssistant
        recommendations={gapPolicyRecommendations}
        onViewPolicy={openPolicyStore}
        onGenerate={openGeneratePolicies}
      />
    </div>
  );
}

function ResultScreen({
  stats,
  answers,
  notes = {},
  owners = {},
  sections = DEFAULT_SECTIONS,
  submitResult,
  orgName = "",
  departmentId = "",
  cycleId = "",
  departmentLabel,
  onViewPolicy,
  onGenerate,
  storeIndex = null,
  onReset,
}) {
  const navigate = useNavigate();
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);

  const evidenceSubmission = {
    id: submitResult?.assessment_id,
    org_name: submitResult?.org_name || orgName,
    department_id: submitResult?.department_id || departmentId || null,
    department_name: submitResult?.department_name || departmentLabel || "",
    cycle_id: cycleId || null,
    score: submitResult?.score ?? stats.score,
    compliance_label: submitResult?.compliance_label,
    answers,
    notes,
    owners,
    result: submitResult || {},
    created_at: submitResult?.timestamp,
    submitted_at: submitResult?.timestamp,
  };

  const goToSubmission = () => {
    const id = submitResult?.assessment_id;
    if (id) {
      navigate(`/user/gap-assessment/submissions?id=${encodeURIComponent(id)}`);
      return;
    }
    navigate("/user/gap-assessment/submissions");
  };

  const handleEvidencePdf = async () => {
    setExportingPdf(true);
    try {
      await exportGapEvidencePdf(evidenceSubmission);
    } catch (err) {
      showError(err?.message || "Failed to export evidence PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  const handleEvidenceExcel = async () => {
    setExportingExcel(true);
    try {
      await exportGapEvidenceExcel(evidenceSubmission);
    } catch (err) {
      showError(err?.message || "Failed to export evidence Excel");
    } finally {
      setExportingExcel(false);
    }
  };
  const scoreColor =
    stats.score >= 75 ? "#10b981" : stats.score >= 50 ? "#f59e0b" : "#ef4444";
  const scoreLabel =
    submitResult?.compliance_label ||
    (stats.score >= 75
      ? "Strong Compliance"
      : stats.score >= 50
        ? "Partial Compliance"
        : "Critical Gaps Found");

  const resultPolicyRecommendations = collectGapPolicyRecommendations(
    answers,
    sections,
    orgName,
    storeIndex,
  );

  return (
    <div
      className="gap-result-scroll"
      style={{
        flex: 1,
        minHeight: 0,
        background: "#ffffff",
        padding: "20px 20px 32px",
      }}
    >
      <div style={{ maxWidth: 660, width: "100%", margin: "0 auto" }}>
        {/* Hero */}
        <div
          style={{
            borderRadius: 24,
            padding: "32px 24px",
            textAlign: "center",
            background: "#ffffff",
            border: "1px solid rgba(99,102,241,0.15)",
            marginBottom: 16,
          }}
        >
          <div
            style={{
              width: 60,
              height: 60,
              borderRadius: 18,
              background:
                "linear-gradient(135deg, #6366f1 0%, #a855f7 60%, #ec4899 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow:
                "0 8px 32px rgba(99,102,241,0.4), 0 4px 12px rgba(168,85,247,0.3)",
            }}
          >
            <Award size={26} color="#fff" />
          </div>
          <h2
            style={{
              fontSize: 22,
              fontWeight: 800,
              color: "#0f172a",
              marginBottom: 4,
            }}
          >
            Assessment Complete
          </h2>
          <p style={{ fontSize: 12, color: "#1e293b", marginBottom: 24 }}>
            DPDP Act 2023 - Gap Assessment Results
            {departmentLabel ? (
              <>
                <br />
                <span style={{ fontSize: 11, color: "#6366f1" }}>
                  Department: {departmentLabel}
                </span>
              </>
            ) : null}
          </p>

          <div
            style={{
              position: "relative",
              display: "inline-block",
              marginBottom: 12,
            }}
          >
            <ArcGauge
              value={stats.score}
              size={120}
              stroke={11}
              color={scoreColor}
            />
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span
                style={{
                  fontSize: 28,
                  fontWeight: 800,
                  color: "#0f172a",
                  lineHeight: 1,
                }}
              >
                {stats.score}%
              </span>
              <span style={{ fontSize: 10, fontWeight: 600, color: "#0f172a" }}>
                compliance
              </span>
            </div>
          </div>
          <div style={{ fontSize: 13, fontWeight: 700, color: scoreColor }}>
            {scoreLabel}
          </div>
        </div>

        {/* Stats */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 10,
            marginBottom: 16,
          }}
        >
          {[
            { l: "Yes", v: stats.yes, c: "#047857", I: Check },
            { l: "Partially", v: stats.partial, c: "#b45309", I: Minus },
            { l: "No", v: stats.no, c: "#b91c1c", I: X },
            { l: "N/A", v: stats.na, c: "#334155", I: HelpCircle },
          ].map((x) => (
            <div
              key={x.l}
              style={{
                borderRadius: 16,
                padding: "16px 12px",
                textAlign: "center",
                background: x.c + "0e",
                border: `1px solid ${x.c}25`,
              }}
            >
              <x.I
                size={18}
                style={{ color: x.c, display: "block", margin: "0 auto 6px" }}
              />
              <div style={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>
                {x.v}
              </div>
              <div style={{ fontSize: 10, color: "#1e293b", marginTop: 2 }}>
                {x.l}
              </div>
            </div>
          ))}
        </div>

        {/* Section scores */}
        <div
          style={{
            borderRadius: 20,
            padding: "20px",
            background: "#ffffff",
            border: "1px solid rgba(99,102,241,0.12)",
            marginBottom: 16,
          }}
        >
          <h3
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: "#1e293b",
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 16,
            }}
          >
            Section-wise Compliance
          </h3>
          {sections.map((s) => {
            const pct = sectionPct(s, answers);
            const c = pct >= 75 ? "#10b981" : pct >= 40 ? "#f59e0b" : "#ef4444";
            return (
              <div
                key={s.id}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                {s.icon && (
                  <s.icon
                    size={12}
                    style={{ color: s.accent, flexShrink: 0 }}
                  />
                )}
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: "#0f172a",
                    width: 180,
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {s.title}
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 5,
                    borderRadius: 9,
                    background: "#ffffff",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      background: c,
                      borderRadius: 9,
                      transition: "width 1.2s cubic-bezier(0.4,0,0.2,1)",
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: 10,
                    color: c,
                    fontWeight: 700,
                    width: 30,
                    textAlign: "right",
                    flexShrink: 0,
                  }}
                >
                  {pct}%
                </span>
              </div>
            );
          })}
        </div>

        {submitResult?.critical_gaps?.length > 0 && (
          <div
            style={{
              borderRadius: 20,
              padding: "20px",
              background: "#fff1f2",
              border: "1px solid #fecdd3",
              marginBottom: 16,
            }}
          >
            <h3
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#b91c1c",
                marginBottom: 12,
              }}
            >
              Critical Gaps ({submitResult.critical_gaps.length})
            </h3>
            {submitResult.critical_gaps.map((g) => (
              <div key={g.id} style={{ marginBottom: 10 }}>
                <div
                  style={{ fontSize: 11, fontWeight: 600, color: "#0f172a" }}
                >
                  {g.id} - {g.section_title}
                </div>
                <div style={{ fontSize: 10, color: "#334155", marginTop: 2 }}>
                  {g.text}
                </div>
              </div>
            ))}
          </div>
        )}

        <GapResultPolicyRecommendations
          recommendations={resultPolicyRecommendations}
          onViewPolicy={onViewPolicy}
          onGenerate={onGenerate}
        />

        {submitResult?.recommendations?.length > 0 && (
          <div
            style={{
              borderRadius: 20,
              padding: "20px",
              background: "#ffffff",
              border: "1px solid rgba(99,102,241,0.12)",
              marginBottom: 16,
            }}
          >
            <h3
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: "#1e293b",
                marginBottom: 4,
              }}
            >
              Comments & Evidence
            </h3>
            <p style={{ fontSize: 10, color: "#64748b", marginBottom: 12 }}>
              Observations and files captured during the assessment
            </p>
            {submitResult.recommendations.map((rec, i) => (
              <div
                key={isGapRecommendationObject(rec) ? rec.question_id || i : i}
                style={{
                  marginBottom: 12,
                  paddingBottom: 12,
                  borderBottom:
                    i < submitResult.recommendations.length - 1
                      ? "1px solid rgba(99,102,241,0.08)"
                      : "none",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: "#0f172a",
                    marginBottom: 4,
                  }}
                >
                  {gapRecommendationLabel(rec, i)}
                </div>
                {isGapRecommendationObject(rec) && rec.answer && (
                  <div
                    style={{ fontSize: 10, color: "#6366f1", marginBottom: 4 }}
                  >
                    Response: {rec.answer}
                  </div>
                )}
                {isGapRecommendationObject(rec) && rec.comment && (
                  <p
                    style={{
                      fontSize: 11,
                      color: "#334155",
                      marginBottom: 6,
                      lineHeight: 1.5,
                    }}
                  >
                    {rec.comment}
                  </p>
                )}
                {isGapRecommendationObject(rec) && rec.owner && (
                  <div
                    style={{ fontSize: 10, color: "#475569", marginBottom: 4 }}
                  >
                    Owner: {rec.owner}
                  </div>
                )}
                {isGapRecommendationObject(rec) && rec.evidence?.length > 0 && (
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 6 }}
                  >
                    {rec.evidence.map((file) => (
                      <AttachmentPreviewRow
                        key={file.id}
                        file={file}
                        previewPath={gapAttachmentPreviewPath(file)}
                      />
                    ))}
                  </div>
                )}
                {!isGapRecommendationObject(rec) && (
                  <p
                    style={{ fontSize: 11, color: "#334155", lineHeight: 1.5 }}
                  >
                    {rec}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button
            type="button"
            onClick={goToSubmission}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "13px",
              borderRadius: 14,
              background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              border: "none",
              boxShadow: "0 6px 24px rgba(99,102,241,0.38)",
              cursor: "pointer",
            }}
          >
            <Eye size={14} /> View Submission
          </button>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={handleEvidencePdf}
              disabled={exportingPdf || exportingExcel}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "11px 12px",
                borderRadius: 12,
                background: exportingPdf ? "#f1f5f9" : "#eef2ff",
                color: "#4338ca",
                fontSize: 12,
                fontWeight: 700,
                border: "1px solid rgba(99,102,241,0.22)",
                cursor: exportingPdf ? "wait" : "pointer",
                opacity: exportingPdf ? 0.7 : 1,
              }}
            >
              <FileDown size={14} />
              {exportingPdf ? "Generating…" : "Evidence PDF"}
            </button>
            <button
              type="button"
              onClick={handleEvidenceExcel}
              disabled={exportingExcel || exportingPdf}
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 6,
                padding: "11px 12px",
                borderRadius: 12,
                background: exportingExcel ? "#f1f5f9" : "#ecfdf5",
                color: "#047857",
                fontSize: 12,
                fontWeight: 700,
                border: "1px solid rgba(16,185,129,0.25)",
                cursor: exportingExcel ? "wait" : "pointer",
                opacity: exportingExcel ? 0.7 : 1,
              }}
            >
              <FileSpreadsheet size={14} />
              {exportingExcel ? "Generating…" : "Evidence Excel"}
            </button>
          </div>
          <button
            type="button"
            onClick={onReset}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              padding: "12px",
              borderRadius: 14,
              background: "#ffffff",
              color: "#475569",
              fontSize: 13,
              fontWeight: 700,
              border: "1px solid #e2e8f0",
              cursor: "pointer",
            }}
          >
            <RefreshCw size={14} /> Start New Assessment
          </button>
        </div>
      </div>
    </div>
  );
}
